import { useSelector, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { useAPIReducer } from '~/api/helpers';
import { APIHookResponse } from '~/api/types';
import { MIN_PASSWORD_METER_LENGTH } from '~/components/password/constants';
import { ConfigType } from '~/components/password/types';
import { passwordIsValid as validator } from '~/components/password/validator';
import { FORM_ERROR_MESSAGES } from '~/constants';
import { Actions } from '~/store/password/actions';
import { getPasswordRequestStatus } from '~/store/password/selectors';
import { get } from '~/utils/object';

type FormValues = {
  newPassword: string;
  confirmedPassword: string;
};

export const usePasswordRules = (
  resetToken: string | null | undefined,
): APIHookResponse => {
  const { success, error } = useSelector(getPasswordRequestStatus);
  const useAPI = useAPIReducer(Actions.PASSWORD_RULES_REQUEST, { resetToken });
  const apiState = useAPI(success, error);
  return apiState;
};

type PasswordResetDispatch = {
  password: string;
};

export const usePasswordReset = (resetToken: string) => {
  const dispatch = useDispatch();
  return ({ password }: PasswordResetDispatch): void => {
    dispatch(Actions.RESET_PASSWORD_REQUEST({ resetToken, password }));
  };
};

const confirmedPassword = {
  confirmedPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], FORM_ERROR_MESSAGES.PASSWORD.MATCH)
    .required(FORM_ERROR_MESSAGES.PASSWORD.MATCH),
};

export const useValidationSchema = (currentRuleSet: ConfigType) => {
  const usePasswordMeter = get(currentRuleSet, 'usePasswordStrength', false);

  const rulesValidationSchema: yup.ObjectSchema<FormValues> = yup
    .object()
    .shape({
      newPassword: yup
        .string()
        .required(FORM_ERROR_MESSAGES.PASSWORD.REQUIRED)
        .test('newPassword', FORM_ERROR_MESSAGES.PASSWORD.REQUIREMENTS, value =>
          validator(currentRuleSet, value),
        )
        .min(
          currentRuleSet.minLength,
          `Minimum of ${currentRuleSet.minLength} characters`,
        ),
      ...confirmedPassword,
    });

  const meterValidationSchema: yup.ObjectSchema<FormValues> = yup
    .object()
    .shape({
      newPassword: yup
        .string()
        .required(FORM_ERROR_MESSAGES.PASSWORD.REQUIRED)
        .min(MIN_PASSWORD_METER_LENGTH, FORM_ERROR_MESSAGES.PASSWORD.MIN),
      ...confirmedPassword,
    });

  return usePasswordMeter ? meterValidationSchema : rulesValidationSchema;
};
